import React from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { DeviceFormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import DelphiConnectionForm from "./DelphiConnectionForm";
import {
  STATUS_IDS,
  STATUS_LABELS,
  ACTION_IDS,
  TableEmptyMessage,
  isConnectionPasswordVisible,
  isSet,
  statusIsEditable,
  CONNECTION_STATUS_LABELS,
} from "../../../../components/Utils";
import TablePageReport from "../../../../components/TablePageReport";
import {
  PermissionResource,
  PermissionSection,
  Resources,
} from "../../../Permissions";
import { getITConnectionFormFlags } from "../Utils";

function DelphiConnectionListForm({
  forwardedRef,
  id = "delphiConnectionListForm",
  className,
  kdlCompanyList,
  userCompany,
  model,
  disableDelphi,
  onChange,
  rows,
  disabled,
}) {
  const formRef = React.useRef(null);
  const [state, setState] = React.useState({
    items: (model || []).map((item, index) => ({ ...item, index })),
    addDisabled: disableDelphi,
    first: 0,
    localStateChanged: false,
  });

  React.useEffect(() => {
    setState((prev) => ({
      ...prev,
      addDisabled: disableDelphi,
    }));
  }, [disableDelphi]);

  const getModel = React.useCallback(() => {
    const { items } = state;
    const model = items.map((item) => ({ ...item }));
    return model;
  }, [state]);
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
  }));

  const onSave = React.useCallback(
    (onHide, updating) => {
      const currentModel = formRef.current.getModel();
      if (!currentModel) {
        return;
      }
      currentModel.status_id = updating ? STATUS_IDS.UPDATING : STATUS_IDS.NEW;
      currentModel.action_id = updating ? ACTION_IDS.UPDATE : ACTION_IDS.CREATE;
      currentModel.status = CONNECTION_STATUS_LABELS[currentModel.status_id];
      const { items } = state;
      let localStateChanged = false;
      if (
        currentModel.index !== undefined &&
        items[currentModel.index] !== undefined
      ) {
        if (
          model &&
          model[currentModel.index] &&
          model[currentModel.index].permissions === currentModel.permissions
        ) {
          // no local changes made
          currentModel.action_id = model[currentModel.index].action_id;
          currentModel.status_id = model[currentModel.index].status_id;
          currentModel.status = model[currentModel.index].status;
        } else {
          // local changes made
          localStateChanged = true;
        }
        items[currentModel.index] = currentModel;
      } else {
        currentModel.index = items.length;
        items.push(currentModel);
      }
      setState((prev) => ({ ...prev, items, localStateChanged }));
      onHide && onHide();
    },
    [formRef, state, model],
  );

  const onDelete = React.useCallback(
    (selected) => {
      if (selected.id) {
        const { items } = state;
        if (items[selected.index]) {
          items[selected.index].status_id = STATUS_IDS.DELETING;
          items[selected.index].action_id = ACTION_IDS.DELETE;
          items[selected.index].status = null; // [STATUS_IDS.DELETING];
          setState((prev) => ({ ...prev, items, localStateChanged: true }));
          onChange && onChange(items);
        } else {
          console.error(
            "<< delphi connection to be deleted was not found",
            state.items,
          );
        }
      } else {
        // delete local changes quietly
        const items = state.items.filter((_, i) => i !== selected.index);
        setState((prev) => ({ ...prev, items }));
        onChange && onChange(items);
      }
    },
    [state, onChange],
  );

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      const data = items.map((item, index) => ({
        ...item,
        index,
        status: STATUS_LABELS[item.status_id || STATUS_IDS.NEW],
      }));
      return (
        <DataTable
          value={data}
          rows={rows}
          paginator={data.length > rows}
          first={state.first}
          onPage={(event) => setState({ first: event.first })}
          paginatorLeft={
            <TablePageReport
              className="pad-lft"
              totalRecords={data.length}
              filteredRecords={data.length}
              first={state.first}
              rows={rows}
              emptyText="Keine Zugänge"
              itemName="Zugang"
              itemNamePlural="Zugänge"
            />
          }
          emptyMessage={<TableEmptyMessage itemNamePlural="IT-Zugänge" min />}
        >
          <Column
            field="parentCompanyName"
            header="Vertragspartner"
            sortable={data.length > 1}
            filter={data.length > rows}
            filterMatchMode="contains"
          />
          <Column
            field="permissions"
            header="Rechte"
            sortable={data.length > 1}
            filter={data.length > rows}
            filterMatchMode="contains"
          />
          <Column
            field="username"
            header="Kennung"
            sortable={data.length > 1}
            filter={data.length > rows}
            filterMatchMode="contains"
          />
          <Column
            field="status"
            header="Status"
            sortable={data.length > 1}
            filter={data.length > rows}
            filterMatchMode="contains"
          />
          <Column
            body={actionTemplate}
            style={{ textAlign: "center", width: "8em" }}
          />
        </DataTable>
      );
    },
    [state, rows],
  );

  const isEditDisabled = React.useCallback(
    (item) => {
      if (state.localStateChanged && item.status_id !== STATUS_IDS.DELETING) {
        return false;
      }
      if (
        isSet(item.id) &&
        model &&
        model[item.index] &&
        item.status_id !== model[item.index].status_id &&
        item.status_id !== STATUS_IDS.DELETING
      ) {
        return false;
      }
      console.log("checking statusIsEditable for item:", item, "disabled:", disabled);
      return !statusIsEditable(item.status_id, item.id, true) || disabled;
      // return (item.id && item.status_id !== STATUS_IDS.FINISHED) || disabled;
    },
    [state, disabled, model],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const connection = item || {
        parent_company_id: kdlCompanyList.length > 0 ? kdlCompanyList[0].id : 0,
        username: "",
        permissions: "",
        status_id: STATUS_IDS.NEW,
      };
      // const saveDisabled = connection.status_id !== STATUS_IDS.NEW;
      // const updateDisabled = (!isSet(connection.id) || isEditDisabled(connection)) || !saveDisabled;
      const { saveDisabled, updateDisabled } = getITConnectionFormFlags(connection, isEditDisabled);
      // Collect permissions already used by other (non-deleting) connections
      // so they can be hidden from the dropdown. The connection currently being
      // edited is excluded so its own permission remains selectable.
      const usedPermissions = state.items
        .filter(
          (it) =>
            it.index !== connection.index /*&&
            it.status_id !== STATUS_IDS.DELETING,*/
        )
        .map((it) => {
          if (!it.permissions) return "";
          // permissions may be stored as comma-separated string; take the first token
          return String(it.permissions).split(", ")[0];
        })
        .filter((p) => p && p.length > 0);
      return (
        <div>
          <DelphiConnectionForm
            ref={formRef}
            model={connection}
            kdlCompanyList={kdlCompanyList}
            userCompany={userCompany}
            usedPermissions={usedPermissions}
            // requireDescription={requireDescription}
            // defaultDescription={defaultDescription}
            readOnly={disabled || (updateDisabled && saveDisabled)}
            key={Math.random()}
          />
          <DeviceFormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onUpdate={() => onSave(onHide, true)}
            onCancel={onHide}
            saveDisabled={saveDisabled}
            updateDisabled={updateDisabled}
            saveHidden={disabled || saveDisabled}
            updateHidden={disabled || updateDisabled}
            saveBtnLabel="Beantragen"
            updateBtnLabel="Ändern"
            cancelBtnLabel={
              disabled || (updateDisabled && saveDisabled)
                ? "Schließen"
                : undefined
            }
            saveBtnName="saveUserDelphiAccBtn"
            updateBtnName="updateUserDelphiAcceBtn"
            cancelBtnName="cancelUserDelphiAccBtn"
          />
        </div>
      );
    },
    [kdlCompanyList, userCompany, disabled, onSave, isEditDisabled],
  );

  const isDeleteDisabled = React.useCallback(
    (item) => {
      if (
        isSet(item.id) &&
        model &&
        model[item.index] &&
        item.status_id !== model[item.index].status_id &&
        item.status_id !== STATUS_IDS.DELETING
      ) {
        return false;
      }
      return !statusIsEditable(item.status_id, item.id, true) || disabled;
    },
    [disabled, model],
  );

  const getItemFormTitle = React.useCallback(
    (connection) => {
      let action = "bearbeiten";
      if (!connection) {
        action = "anlegen";
      } else if (isEditDisabled(connection)) {
        action = "ansehen";
      }
      return <h3 className="no-margin">{`Delphi-Zugang ${action}`}</h3>;
    },
    [isEditDisabled],
  );

  return (
    <ItemListManager
      id={id}
      key={Math.random()}
      itemPanelStyle={{ width: "640px" }}
      className={className}
      title={
        <h4 className="no-margin-top" style={{ height: "36px" }}>
          {"Delphi-Zugänge"}
        </h4>
      }
      addBtnLabel="Hinzufügen"
      addBtnName="addWorkerDelphiConnectionBtn"
      addBtnDisabled={state.addDisabled}
      addBtnHidden={disabled}
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="delphiConnection"
      isEditDisabled={isEditDisabled}
      itemDeleteable={!disabled}
      hideDisabledDelete
      isDeleteDisabled={isDeleteDisabled}
      deleteTitle="Delphi-Zugang löschen"
      getDeleteText={(item) =>
        `Wollen Sie den Delphi-Zugang (Kennung: '${item.username}', Vertragspartner: ${item.parentCompanyName}) unwiderruflich löschen?`
      }
      onDelete={onDelete}
    />
  );
}

DelphiConnectionListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.array,
  kdlCompanyList: PropTypes.array,
  userCompany: PropTypes.object,
  disableDelphi: PropTypes.bool,
  canReadAdminData: PropTypes.bool,
  rows: PropTypes.number,
  disabled: PropTypes.bool,
};

export default React.forwardRef((props, ref) => {
  return <DelphiConnectionListForm {...props} forwardedRef={ref} />;
});

export const DelphiConnectionPanel = ({
  delphiConnections,
  rows,
  id = "delphi-zugaenge",
}) => {
  const [first, setFirst] = React.useState(0);
  const onPage = (event) => {
    setFirst(event.first);
  };

  const data = delphiConnections.map((item) => ({
    ...item,
    password: isConnectionPasswordVisible(item.modified_date)
      ? item.password
      : "",
  }));
  const columnProps = {
    sortable: data.length > 1,
    filter: data.length > 10,
    filterMatchMode: "contains",
  };
  return (
    <PermissionResource resource={Resources.Profile.DelphiConnections}>
      <PermissionSection
        height="auto"
        id={id}
        className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
        isUnterWizardTitle
        idOffset={-90}
      >
        <h4 className="no-margin pad-big-top pad-btm">{"Delphi-Zugänge"}</h4>
        <DataTable
          value={delphiConnections}
          rows={rows}
          paginator={delphiConnections.length > rows}
          first={first}
          onPage={onPage}
          emptyMessage={
            <TableEmptyMessage
              prefix="Kein"
              itemNamePlural="Delphi-Zugänge"
              min
            />
          }
        >
          <Column
            field="parentCompanyName"
            header="Vertragspartner"
            {...columnProps}
          />
          <Column
            field="permissions"
            header="Rechte"
            {...columnProps}
            style={{ width: "14em" }}
          />
          <Column
            field="username"
            header="Kennung"
            {...columnProps}
            style={{ width: "14em" }}
          />
          <Column
            field="password"
            header="Passwort"
            {...columnProps}
            style={{ width: "12em" }}
          />
          <Column
            field="status"
            header="Status"
            {...columnProps}
            style={{ width: "10em", textAlign: "center" }}
          />
        </DataTable>
      </PermissionSection>
    </PermissionResource>
  );
};
